import { memberLetter } from '@jackedlogs/shared';
import type { DraftEntry } from './state/loggerDraft';

// Superset notebook (timeline logger, slice 5) — pure helpers behind the
// FocusPanel's tabbed superset/circuit "notebook". When the active exercise
// belongs to a group, the timeline screen shows the group's members as tabs
// (A/B/C…) and auto-advances focus to the next member after each LOG SET, so a
// superset is logged round-robin (1A → 1B → 1A …) without the athlete hunting
// for the next exercise. Data model untouched — membership is the existing
// `entry.group` linkage; this only derives view state + the next focus target.

type GroupMemberState = 'done' | 'inProgress' | 'notDone';

export interface GroupMember {
  entry: DraftEntry;
  /** Index into the draft's entries[] — the dispatch/bind coordinate. */
  entryIdx: number;
  /** Position within the group → A, B, C … (the notebook tab letter). */
  letter: string;
  logged: number;
  planned: number;
  /** Three states, never conflated (§7.3): a not-done member is not failed. */
  state: GroupMemberState;
}

function memberState(logged: number, planned: number): GroupMemberState {
  if (planned > 0 && logged >= planned) return 'done';
  if (logged > 0) return 'inProgress';
  return 'notDone';
}

/** The members of `groupId`, in workout (entry) order, each tagged with its
 *  notebook letter (A, B, …) and set progress. Empty when the group is absent.
 *  `minMembers` (default 2) gates the result: auto-advance treats a lone entry
 *  as "not a notebook" (2), but the group-line HEADER shows from the first
 *  member (1) so creating a superset shows the header immediately — Antenore
 *  2026-06-01: "il primo esercizio non ha l'header superset". */
export function groupMembersOf(
  entries: DraftEntry[],
  groupId: string | null | undefined,
  minMembers = 2,
): GroupMember[] {
  if (!groupId) return [];
  const members: GroupMember[] = [];
  entries.forEach((entry, entryIdx) => {
    if (entry.group?.id !== groupId) return;
    const planned = entry.sets.length;
    const logged = entry.sets.filter((s) => s.completedAt !== null).length;
    members.push({
      entry,
      entryIdx,
      letter: memberLetter(members.length),
      logged,
      planned,
      state: memberState(logged, planned),
    });
  });
  return members.length >= minMembers ? members : [];
}

/** Round-robin auto-advance: after logging `justLoggedSetIdx` on the member at
 *  `currentEntryIdx`, returns the ENTRY INDEX of the next group member that
 *  still has a pending set (searching forward from the current member,
 *  wrapping), or null when no member has a pending set left (the round is
 *  done). The just-logged set is treated as completed even though the reducer
 *  dispatch hasn't applied yet, so the final set of a superset doesn't bounce
 *  focus back onto itself.
 *
 *  ⛔ It returns the index it already found, not `entry.exerciseId` (LG-39).
 *  Handing back the slug forced the caller to findIndex its way back to a row,
 *  and that search is ambiguous the moment a circuit holds the same exercise
 *  twice — it would hop to the wrong member, and the "don't bounce onto
 *  yourself" guard would misfire on the twin. The answer was already in hand;
 *  it was being thrown away and re-guessed. */
export function nextGroupMemberEntryIdx(
  members: GroupMember[],
  currentEntryIdx: number,
  justLoggedSetIdx: number,
): number | null {
  const n = members.length;
  if (n === 0) return null;
  const pos = members.findIndex((m) => m.entryIdx === currentEntryIdx);
  if (pos < 0) return null;
  for (let step = 1; step <= n; step++) {
    const m = members[(pos + step) % n]!;
    const hasPending = m.entry.sets.some(
      (s, i) =>
        s.completedAt === null &&
        !(m.entryIdx === currentEntryIdx && i === justLoggedSetIdx),
    );
    if (hasPending) return m.entryIdx;
  }
  return null;
}
